import { override, makeObservable } from 'mobx';
import { IdResource, IResourceSession, ResourceSession } from './ResourceSession';
import { AsyncStore } from './AsyncStore';
import { hasValue } from '~/lib/helper/ArrayHelper';

type LoaderFunction<T> = () => Promise<T>;

export type ResourceStoreConfig<T, TInitial> = {
    getResource: LoaderFunction<T>;
    getKey: (item: T) => unknown;
    session?: IResourceSession<T>;
    initialValue: TInitial;
};

export class ResourceStore<T extends object, TInitial extends T | undefined = T> extends AsyncStore<T, TInitial> {
    private session: IResourceSession<T>;

    constructor(config: ResourceStoreConfig<T, TInitial>) {
        super(config.getResource, config.initialValue);
        this.session = config.session ?? new ResourceSession<Exclude<T, undefined>>(config.getKey);
        if (hasValue(config.initialValue)) {
            this.setItem(config.initialValue as Exclude<TInitial, undefined>);
        }
        makeObservable(this);
    }

    @override
    setItem(item: T): T {
        const sessionItem = this.session.updateItem(item);
        this.item = sessionItem;
        return sessionItem;
    }
}

export class IdResourceStore<T extends IdResource> extends ResourceStore<T, undefined> {
    constructor(config: Omit<ResourceStoreConfig<T, undefined>, 'getKey' | 'initialValue'>) {
        super({
            ...config,
            getKey: (x) => x.id,
            initialValue: undefined,
        });
    }
}

export class PreloadedIdResourceStore<T extends IdResource> extends ResourceStore<T> {
    constructor(config: Omit<ResourceStoreConfig<T, T>, 'getKey'>) {
        super({
            ...config,
            getKey: (x) => x.id,
        });
    }
}
